import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Select } from 'antd';
import { CSVLink } from 'react-csv';
import { FileExcelOutlined, ClockCircleOutlined, UserOutlined, DollarOutlined } from '@ant-design/icons';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';

// Extend dayjs with plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(duration);
dayjs.extend(isBetween);

const { Option } = Select;

const API_URL = process.env.REACT_APP_API_URL;

const TeacherApprovedPay = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [periodOptions, setPeriodOptions] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
    
  // Format period from "2025-01-26_2025-02-25" to "26 Jan 2025-25 Feb 2025"
  const formatPeriod = (periodString) => {
    if (!periodString) return '-';
    
    try {
      // Check if period follows the format YYYY-MM-DD_YYYY-MM-DD
      if (periodString.includes('_')) {
        const [startDate, endDate] = periodString.split('_');
        
        // Parse dates
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        
        // Check if parsing was successful
        if (start.isValid() && end.isValid()) {
          return `${start.format('DD MMM YYYY')}-${end.format('DD MMM YYYY')}`;
        }
      }
      
      // Return original if format doesn't match
      return periodString;
    } catch (error) {
      console.error("Error formatting period:", error);
      return periodString;
    }
  };
  
  // Check if a date is within a period range
  const isDateInPeriod = (date, periodStr) => {
    try {
      if (!periodStr || !periodStr.includes('_')) return false;
      
      const [startStr, endStr] = periodStr.split('_');
      const checkDate = dayjs(date);
      const startDate = dayjs(startStr);
      const endDate = dayjs(endStr);
      
      return checkDate.isValid() && 
             startDate.isValid() && 
             endDate.isValid() && 
             checkDate.isBetween(startDate, endDate, 'day', '[]');
    } catch (error) {
      console.error("Error checking if date is in period:", error);
      return false;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/teacher-hours/teacher-approve-payment`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log("✅ API Response:", response.data);

        const today = dayjs();
        let currentPeriod = null;

        // Add a unique identifier to each item
        const dataWithIds = response.data.map((item, index) => {
          // Create a unique ID
          const uniqueId = `row-${index}-${Date.now()}`;
          
          // If period is not already in the data, generate it from date
          let period = item.period; 
          if (!period && item.date) {
            try {
              const dateObj = dayjs(item.date);
              if (dateObj.isValid()) {
                period = dateObj.format('YYYY-MM');
              }
            } catch (e) {
              console.error("Error generating period from date:", e);
            }
          }
          
          // Calculate total payment (hourly rate * hours worked)
          const hourlyRate = parseFloat(item.hourly_rate || 0);
          const hoursWorked = parseFloat(item.hours_worked || 0);
          const totalPayment = !isNaN(hourlyRate) && !isNaN(hoursWorked) 
            ? hourlyRate * hoursWorked 
            : 0;
          
          return {
            ...item,
            period: period || '-',
            total_payment: totalPayment,
            uniqueRowId: uniqueId
          };
        });

        // Extract unique periods for the dropdown
        const uniquePeriods = Array.from(
          new Set(dataWithIds.map(item => item.period).filter(Boolean))
        ).sort((a, b) => {
          // Sort periods, newest first
          if (a.includes('_') && b.includes('_')) {
            // If they're in date range format (YYYY-MM-DD_YYYY-MM-DD)
            return dayjs(b.split('_')[0]).diff(dayjs(a.split('_')[0]));
          } else if (a.includes('-') && b.includes('-')) {
            // If they're in YYYY-MM format
            return b.localeCompare(a);
          }
          return a.localeCompare(b);
        });
        
        // Find the period that contains today's date
        for (const period of uniquePeriods) {
          if (period.includes('_') && isDateInPeriod(today, period)) {
            currentPeriod = period;
            break;
          }
        }
        
        // If no period contains today, use the most recent period
        if (!currentPeriod && uniquePeriods.length > 0) {
          currentPeriod = uniquePeriods[0];
        }
        
        setPeriodOptions(uniquePeriods);
        setSelectedPeriod(currentPeriod);
        
        setData(dataWithIds);
        
        // Filter by the current period if found
        if (currentPeriod) {
          setFiltered(dataWithIds.filter(item => item.period === currentPeriod));
        } else {
          setFiltered(dataWithIds);
        }
      } catch (error) {
        console.error('❌ Error fetching approved payments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);  // Empty dependency array to only run once on mount

  // Apply filters when selectedPeriod changes
  useEffect(() => {
    // Guard against empty data
    if (!data || data.length === 0) {
      setFiltered([]);
      return;
    }
    
    let result = [...data];

    if (selectedPeriod) {
      result = result.filter(item => item.period === selectedPeriod);
    }

    setFiltered(result);
  }, [selectedPeriod, data]);

  // Handler for period change
  const handlePeriodChange = (value) => {
    console.log("Period changed to:", value);
    setSelectedPeriod(value);
  };

  // Convert hours in decimal format to HH:MM
  const formatHoursToHHMM = (hoursDecimal) => {
    if (!hoursDecimal) return "0:00";
    
    const hours = parseFloat(hoursDecimal);
    if (isNaN(hours)) return "0:00";
    
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    
    return `${wholeHours}:${minutes.toString().padStart(2, '0')}`;
  };

  // Calculate end time based on start time and hours
  const calculateEndTime = (startTime, hoursWorked) => {
    if (!startTime) return null;
    
    try {
      // Parse the start time
      let startDateTime;
      
      // Handle different time formats
      if (startTime.includes('T')) {
        // ISO format (2023-01-01T13:00:00)
        startDateTime = dayjs(startTime);
      } else if (startTime.includes(':')) {
        // Time only format (13:00:00 or 13:00)
        const [hours, minutes] = startTime.split(':').map(Number);
        startDateTime = dayjs().hour(hours).minute(minutes).second(0);
      } else {
        return null;
      }
      
      if (!startDateTime.isValid()) return null;
      
      // Parse hours worked
      const hoursNum = parseFloat(hoursWorked);
      if (isNaN(hoursNum)) return null;
      
      // Calculate minutes
      const minutesTotal = Math.round(hoursNum * 60);
      
      // Add duration to start time
      const endDateTime = startDateTime.add(minutesTotal, 'minute');
      
      return endDateTime;
    } catch (error) {
      console.error("Error calculating end time:", error);
      return null;
    }
  };

  // Format time for display (HH:MM)
  const formatTimeDisplay = (timeString) => {
    if (!timeString) return "-";
    
    try {
      if (typeof timeString === 'string' && timeString.includes(':')) {
        // If it's a time string like "13:00:00"
        return timeString.split(':').slice(0, 2).join(':');
      } else if (dayjs.isDayjs(timeString)) {
        // If it's a dayjs object
        return timeString.format('HH:mm');
      }
      return "-";
    } catch (error) {
      return "-";
    }
  };

  // Get teacher full name
  const getTeacherFullName = (record) => {
    // Check for different name field possibilities
    const firstName = record.first_name || record.firstname || '';
    const lastName = record.last_name || record.lastname || '';
    const fullName = record.teacher_name || record.full_name || '';
    const nickname = record.nickname ? `(${record.nickname})` : '';
    
    // If we have a full name already, use it
    if (fullName) {
      return nickname ? `${fullName} ${nickname}` : fullName;
    }
    
    // Otherwise combine first and last name
    if (firstName || lastName) {
      const name = `${firstName} ${lastName}`.trim();
      return nickname ? `${name} ${nickname}` : name;
    }
    
    // If no name found
    return "-";
  };

  const columns = [
    {
        title: t('Period'),
        dataIndex: 'period',
        key: 'period',
        render: period => formatPeriod(period)
      },
    {
      title: t('Date'),
      dataIndex: 'date',
      key: 'date',
      render: date => {
        try {
          return date ? dayjs(date).format('DD-MMM-YYYY') : '-';
        } catch (e) {
          return '-';
        }
      }
    },

    { 
      title: t('Class Code'), 
      dataIndex: 'class_code', 
      key: 'class_code',
      render: code => code || '-'
    },
    { 
      title: t('Subject'), 
      dataIndex: 'subject_name', 
      key: 'subject_name',
      render: subject => subject || '-'
    },
    {
      title: t('Teacher'),
      key: 'teacher',
      render: (_, record) => (
        <span>
          <UserOutlined style={{ marginRight: 6, color: '#1890ff' }} />
          {getTeacherFullName(record)}
        </span>
      ),
    },
    {
      title: t('Time'),
      key: 'time',
      render: (_, record) => {
        // Get the start time - could be in schedule_time or start_time
        const startTime = record.schedule_time || record.start_time;
        
        if (!startTime) return "-";
        
        // Calculate end time
        const endTime = calculateEndTime(startTime, record.hours_worked);
        
        // Format for display
        const startDisplay = formatTimeDisplay(startTime);
        const endDisplay = endTime ? formatTimeDisplay(endTime) : "-";
        
        return (
          <span>
            <ClockCircleOutlined style={{ marginRight: 6, color: '#52c41a' }} />
            {`${startDisplay} - ${endDisplay}`}
          </span>
        );
      }
    },
    { 
      title: t('Hours'), 
      dataIndex: 'hours_worked', 
      key: 'hours_worked',
      render: hours => formatHoursToHHMM(hours)
    },
    {
      title: t('Rate'),
      dataIndex: 'hourly_rate',
      key: 'hourly_rate',
      render: rate => {
        const numRate = parseFloat(rate);
        return !isNaN(numRate) ? numRate.toFixed(2) : '0.00';
      }
    },
    {
      title: t('Total Paid'),
      key: 'total',
      dataIndex: 'total_payment',
      render: (total, record) => {
        // We can either use the pre-calculated total or calculate it here
        let totalPayment = total;
        
        // If total wasn't pre-calculated, calculate it here as fallback
        if (totalPayment === undefined || totalPayment === null) {
          const hourlyRate = parseFloat(record.hourly_rate || 0);
          const hoursWorked = parseFloat(record.hours_worked || 0);
          totalPayment = !isNaN(hourlyRate) && !isNaN(hoursWorked) 
            ? hourlyRate * hoursWorked 
            : 0;
        }
        
        return (
          <span style={{ fontWeight: 'bold' }}>
            <DollarOutlined style={{ marginRight: 6 }} />
            {parseFloat(totalPayment).toFixed(2)}
          </span>
        );
      },
      sorter: (a, b) => {
        const totalA = parseFloat(a.total_payment || 0);
        const totalB = parseFloat(b.total_payment || 0);
        return totalA - totalB;
      }
    },
  ];

  const csvData = filtered.map(row => {
    // Calculate end time for CSV
    const startTime = row.schedule_time || row.start_time;
    const endTime = calculateEndTime(startTime, row.hours_worked);
    
    // Get total payment
    const totalPayment = row.total_payment !== undefined ? row.total_payment : 
      parseFloat(row.hourly_rate || 0) * parseFloat(row.hours_worked || 0);
    
    return {
      Date: row.date ? dayjs(row.date).format('YYYY-MM-DD') : '',
      Period: formatPeriod(row.period || ''),
      ClassCode: row.class_code || '',
      Subject: row.subject_name || '',
      Teacher: getTeacherFullName(row),
      StartTime: formatTimeDisplay(startTime),
      EndTime: endTime ? formatTimeDisplay(endTime) : '',
      Hours: row.hours_worked || '',
      HourlyRate: parseFloat(row.hourly_rate || 0).toFixed(2),
      Total: parseFloat(totalPayment).toFixed(2)
    };
  });

  // Calculate grand total
  const grandTotal = filtered.reduce((sum, item) => {
    const total = parseFloat(item.total_payment || 0);
    return sum + (isNaN(total) ? 0 : total);
  }, 0);


  return (
    <div>
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            style={{ width: 300, marginRight: 16 }}
            placeholder={t('Select Period')}
            value={selectedPeriod}
            onChange={handlePeriodChange}
            allowClear
          >
            {periodOptions.map(period => (
              <Option key={period} value={period}>
                {formatPeriod(period)}
              </Option>
            ))}
          </Select>
        </Space>
        <CSVLink data={csvData} filename={`teacher_approved_pay_${selectedPeriod || 'all'}.csv`}>
              <Button icon={<FileExcelOutlined />} type="primary">{t('Export to CSV')}</Button>
            </CSVLink>
        <div style={{ marginBottom: 10 }}>
          {filtered.length > 0 ? (
            <span>Showing {filtered.length} records</span>
          ) : (
            <span style={{ color: '#f5222d' }}>No records found for this period. Try selecting a different period.</span>
          )}
        </div>

        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="uniqueRowId"
          loading={loading}
          bordered
          pagination={{ pageSize: 50 }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={8} align="right">
                  <strong>{t('Grand Total')}:</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8}>
                  <strong style={{ color: '#52c41a' }}>{grandTotal.toFixed(2)}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
    </div>
  );
};

export default withRoleAccess(TeacherApprovedPay, ['superadmin']);